// pages/app/Setup.tsx — mandatory setup gate: configure Key1 (spec §4.4).
// Model / reasoning / context-window dropdowns are populated by QUERYING the provider.
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Seo } from '../../components/Seo';
import { Wordmark } from '../../components/Wordmark';
import { useServices } from '../../app/AppContext';
import { useSetupContext } from '../../app/SetupContext';
import { runCopilotDeviceFlow } from '../../lib/copilotOauth';
import type { ModelCapabilities, ModelInfo } from '../../lib/api';

const PROVIDERS = [
  { id: 'openai', label: 'OpenAI', cred: 'apiKey' as const },
  { id: 'claude', label: 'Claude (Anthropic)', cred: 'apiKey' as const },
  { id: 'github_copilot_oauth', label: 'GitHub Copilot (OAuth)', cred: 'oauthToken' as const },
];

function msg(e: unknown): string {
  return e instanceof Error ? e.message : 'Something went wrong';
}

export function Setup() {
  const { api } = useServices();
  const { status, refresh } = useSetupContext();
  const navigate = useNavigate();

  const [provider, setProvider] = useState('openai');
  const [credential, setCredential] = useState('');
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [model, setModel] = useState('');
  const [caps, setCaps] = useState<ModelCapabilities | null>(null);
  const [reasoning, setReasoning] = useState('');
  const [contextWindow, setContextWindow] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userCode, setUserCode] = useState<string | null>(null);
  const [verificationUri, setVerificationUri] = useState('');
  const [oauthBusy, setOauthBusy] = useState(false);
  const [reconfigure, setReconfigure] = useState(false);

  const key1Set = Boolean(status?.key1Provider);
  const credType = PROVIDERS.find((p) => p.id === provider)?.cred ?? 'apiKey';

  useEffect(() => {
    if (key1Set && models.length === 0) {
      api
        .listModels()
        .then((r) => setModels(r.models))
        .catch((e) => setError(msg(e)));
    }
  }, [key1Set, models.length, api]);

  async function submitKey1(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.setKey1({ provider, apiKey: credential });
      await refresh();
      setReconfigure(false);
    } catch (err) {
      setError(msg(err));
    } finally {
      setBusy(false);
    }
  }

  /** GitHub Copilot: open the browser, show the code, poll until authorized (spec §4.4). */
  async function signInWithGitHub() {
    setError(null);
    setUserCode(null);
    setOauthBusy(true);
    try {
      await runCopilotDeviceFlow({
        start: () => api.startCopilotOauth(),
        poll: (dc) => api.pollCopilotOauth(dc),
        open: (url) => window.open(url, '_blank', 'noopener'),
        sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
        onCode: (code, uri) => {
          setUserCode(code);
          setVerificationUri(uri);
        },
      });
      await refresh();
      setReconfigure(false);
    } catch (err) {
      setError(msg(err));
    } finally {
      setOauthBusy(false);
      setUserCode(null);
    }
  }

  async function onModelChange(id: string) {
    setModel(id);
    setCaps(null);
    if (!id) return;
    try {
      const c = await api.getModelCapabilities(id);
      setCaps(c);
      setReasoning(c.reasoningLevels[0]);
      setContextWindow(c.contextWindows[0]);
    } catch (err) {
      setError(msg(err));
    }
  }

  async function submitSelection(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.selectKey1({ model, reasoning, contextWindow: Number(contextWindow) });
      await refresh();
      navigate('/app');
    } catch (err) {
      setError(msg(err));
    } finally {
      setBusy(false);
    }
  }

  const providerLabel =
    PROVIDERS.find((p) => p.id === (status?.key1Provider ?? provider))?.label ?? provider;
  const showCredentialPhase = !key1Set || reconfigure;

  return (
    <div className="mx-auto max-w-xl animate-fade-in py-6 sm:py-10">
      <Seo title="Setup — UPLOAD" description="Configure your LLM provider." path="/app/setup" />
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          Set up <Wordmark className="text-xl" />
        </h1>
        <p className="mt-2 text-sm text-fg-muted">
          Connect your own LLM (Key1). It powers extraction, refinement, and chat — your key is
          verified, encrypted at rest, and never leaves the server.
        </p>
      </div>

      <ol className="mb-6 flex items-center gap-3 text-sm" aria-hidden="true">
        <li
          className={`flex items-center gap-2 ${showCredentialPhase ? 'text-brand' : 'text-fg-muted'}`}
        >
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${showCredentialPhase ? 'bg-brand text-brand-fg' : 'bg-surface-3'}`}
          >
            1
          </span>
          Provider
        </li>
        <span className="h-px w-8 bg-line/15" />
        <li
          className={`flex items-center gap-2 ${!showCredentialPhase ? 'text-brand' : 'text-fg-muted'}`}
        >
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${!showCredentialPhase ? 'bg-brand text-brand-fg' : 'bg-surface-3'}`}
          >
            2
          </span>
          Model
        </li>
      </ol>

      {error ? (
        <p
          role="alert"
          className="mb-4 rounded-xl border border-danger/20 bg-danger/10 px-3.5 py-2.5 text-sm text-danger"
        >
          {error}
        </p>
      ) : null}

      {showCredentialPhase ? (
        <div className="card space-y-5">
          <label className="block">
            <span className="field-label">Provider</span>
            <select
              className="select"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              aria-label="Provider"
            >
              {PROVIDERS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <span className="field-hint">
              Bring your own key — OpenAI, Anthropic, or GitHub Copilot.
            </span>
          </label>

          {credType === 'apiKey' ? (
            <form className="space-y-4" onSubmit={submitKey1}>
              <label className="block">
                <span className="field-label">API key</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="off"
                  value={credential}
                  onChange={(e) => setCredential(e.target.value)}
                  placeholder="sk-…"
                  aria-label="Credential"
                  required
                />
                <span className="field-hint">Encrypted at rest; used only for your requests.</span>
              </label>
              <div className="flex gap-2">
                <button type="submit" className="btn flex-1" disabled={busy}>
                  {busy ? 'Verifying…' : 'Verify & continue'}
                </button>
                {key1Set ? (
                  <button type="button" className="btn-ghost" onClick={() => setReconfigure(false)}>
                    Cancel
                  </button>
                ) : null}
              </div>
            </form>
          ) : (
            <div className="space-y-3">
              {userCode ? (
                <div className="rounded-xl border border-brand/20 bg-brand/5 p-4 text-sm">
                  Enter code{' '}
                  <strong className="font-mono text-base tracking-widest text-brand">
                    {userCode}
                  </strong>{' '}
                  at{' '}
                  <a className="link" href={verificationUri} target="_blank" rel="noreferrer">
                    {verificationUri}
                  </a>{' '}
                  and authorize. Waiting for GitHub…
                </div>
              ) : (
                <p className="text-sm text-fg-muted">
                  Sign in with GitHub to authorize Copilot — a browser tab opens and you enter a
                  short code. Your token is exchanged and stored server-side.
                </p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn flex-1"
                  onClick={() => void signInWithGitHub()}
                  disabled={oauthBusy}
                >
                  {oauthBusy ? 'Waiting for GitHub…' : 'Sign in with GitHub'}
                </button>
                {key1Set ? (
                  <button type="button" className="btn-ghost" onClick={() => setReconfigure(false)}>
                    Cancel
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </div>
      ) : (
        <form className="card space-y-5" onSubmit={submitSelection}>
          <div className="flex items-center justify-between gap-3 rounded-xl border border-line/10 bg-surface-2 px-4 py-3">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-sm font-medium text-fg">
                <span className="h-2 w-2 rounded-full bg-success" /> Connected
              </p>
              <p className="truncate text-xs text-fg-subtle">{providerLabel}</p>
            </div>
            <button
              type="button"
              className="btn-ghost px-3 py-2 text-xs"
              onClick={() => setReconfigure(true)}
            >
              Change provider or key
            </button>
          </div>

          <label className="block">
            <span className="field-label">Model</span>
            <select
              className="select"
              value={model}
              onChange={(e) => void onModelChange(e.target.value)}
              aria-label="Model"
              required
            >
              <option value="">Select a model…</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>

          {caps ? (
            <>
              <label className="block">
                <span className="field-label">Reasoning level</span>
                <select
                  className="select"
                  value={reasoning}
                  onChange={(e) => setReasoning(e.target.value)}
                  aria-label="Reasoning level"
                >
                  {caps.reasoningLevels.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="field-label">Context window</span>
                <select
                  className="select"
                  value={contextWindow}
                  onChange={(e) => setContextWindow(Number(e.target.value))}
                  aria-label="Context window"
                >
                  {caps.contextWindows.map((c) => (
                    <option key={c} value={c}>
                      {c.toLocaleString()} tokens
                    </option>
                  ))}
                </select>
              </label>
              {caps.multimodalAudio ? (
                <p className="flex items-center gap-2 rounded-xl border border-success/20 bg-success/10 px-3 py-2 text-xs text-success">
                  This model accepts audio — voice think-aloud will be enabled.
                </p>
              ) : null}
            </>
          ) : null}

          <button type="submit" className="btn w-full" disabled={busy || !model || !caps}>
            {busy ? 'Saving…' : 'Finish setup'}
          </button>
        </form>
      )}
    </div>
  );
}
